import React, { useState, useEffect } from 'react';
import { communicationsService } from '../../services';

// Voyager Pro / Figma ItineraryManagementPage - new two-way client messaging
// thread for itineraries (no equivalent existed before this session; built
// to mirror Quotes/QuoteMessagesModal.jsx's pattern exactly, backed by the
// same `communications` table with a new itinerary_id column + an
// 'itinerary_message' communication_type value).

const ItineraryMessagesModal = ({ itinerary, isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    if (isOpen && itinerary?.id) {
      fetchMessages();
    }
    // eslint-disable-next-line
  }, [isOpen, itinerary?.id]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await communicationsService.getItineraryMessages(itinerary.id);
      setMessages(response.messages || []);
    } catch (err) {
      console.error('Error fetching itinerary messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    try {
      setSendingReply(true);
      const response = await communicationsService.replyToItineraryMessage(itinerary.id, replyMessage.trim());
      if (response.emailSent) {
        console.log('✅ Email notification sent to client');
      } else if (response.emailError) {
        console.warn('⚠️ Email notification failed:', response.emailError);
        setError(`Reply sent but email notification failed: ${response.emailError}`);
      }
      setReplyMessage('');
      await fetchMessages();
    } catch (err) {
      console.error('Error sending reply:', err);
      setError('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)] flex-shrink-0">
          <h3 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">
            Trip Messages — {itinerary?.title}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors flex-shrink-0">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          <div className="p-4 bg-[var(--muted)]/40 rounded-xl mb-4">
            <div className="text-sm font-semibold text-[var(--foreground)]">{itinerary?.title || 'Trip Itinerary'}</div>
            {itinerary?.client_name && <div className="text-xs text-[var(--muted-foreground)] mt-0.5">Client: {itinerary.client_name}</div>}
          </div>

          {loading ? (
            <div className="text-center py-10 text-sm text-[var(--muted-foreground)]">Loading messages…</div>
          ) : error ? (
            <div className="text-center py-10 text-sm text-red-600">{error}</div>
          ) : messages.length === 0 ? (
            <div className="text-center py-10 text-sm text-[var(--muted-foreground)]">No messages yet for this trip.</div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`p-4 rounded-xl border-l-4 ${message.direction === 'inbound' ? 'bg-blue-50 border-blue-500' : 'bg-emerald-50 border-emerald-500'}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span>{message.direction === 'inbound' ? '📥' : '📤'}</span>
                      <span className="text-sm font-semibold text-[var(--foreground)]">{message.direction === 'inbound' ? 'Client' : 'Agent'}</span>
                    </div>
                    <span className="text-xs text-[var(--muted-foreground)]">{formatDate(message.communication_date)}</span>
                  </div>
                  <p className="text-sm text-[var(--foreground)] leading-relaxed whitespace-pre-wrap">{message.content}</p>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleSendReply} className="mt-6">
            <label className="block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-1.5">Send Reply to Client</label>
            <textarea
              value={replyMessage}
              onChange={e => setReplyMessage(e.target.value)}
              rows={4}
              placeholder="Type your reply message here…"
              disabled={sendingReply}
              className="w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors resize-y"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={sendingReply}
                className="px-5 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={!replyMessage.trim() || sendingReply}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-50"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                {sendingReply ? 'Sending…' : 'Send Reply'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ItineraryMessagesModal;
